import React from "react";
import {
  Box,
  Container,
  SimpleGrid,
  Heading,
  Text,
  Image,
  VStack,
  Badge,
  Button,
  HStack,
  Icon,
  useColorModeValue,
  Flex,
  Progress,
  useToast,
  Grid,
  Divider,
  IconButton,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { Link as RouterLink } from "react-router-dom";
import { 
  FiShoppingBag, 
  FiStar, 
  FiPercent, 
  FiClock, 
  FiShoppingCart,
  FiDroplet,
  FiHeart,
  FiShield,
} from "react-icons/fi";
import { 
  FaMagic, 
  FaLeaf,
  FaGem,
  FaRegClock,
  FaSprayCan,
  FaCertificate,
  FaShippingFast,
} from "react-icons/fa";
import { products } from "../data/products";
import { useCart } from "../context/CartContext";
import type { Product } from "../types";

const MotionBox = motion(Box);

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.5,
    },
  },
};

const Sale: React.FC = () => {
  const { addToCart } = useCart();
  const toast = useToast();
  const bgColor = useColorModeValue("white", "gray.800");
  const textColor = useColorModeValue("gray.600", "gray.300");

  // Filter products with discounts
  const saleProducts = products.filter((product) => product.discount > 0);

  const handleAddToCart = (product: Product) => {
    addToCart(product);
  };

  return (
    <Box py={8}>
      <Container maxW="1400px">
        <VStack spacing={8} align="stretch">
          {/* Sale Header */}
          <Box textAlign="center" py={8}>
            <Heading size="2xl" color="purple.700" mb={4}>
              Luxury Perfume Sale
            </Heading>
            <Text fontSize="xl" color="purple.500" mb={6}>
              Discover our exclusive collection of discounted luxury fragrances
            </Text>
            <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={6}>
              <Flex direction="column" align="center" p={4} bg="purple.50" borderRadius="lg">
                <Icon as={FaCertificate} w={8} h={8} color="purple.500" mb={2} />
                <Text fontWeight="bold">Authentic Products</Text>
                <Text fontSize="sm" color="gray.600">100% Genuine</Text>
              </Flex>
              <Flex direction="column" align="center" p={4} bg="purple.50" borderRadius="lg">
                <Icon as={FiPercent} w={8} h={8} color="purple.500" mb={2} />
                <Text fontWeight="bold">Up to 17% Off</Text>
                <Text fontSize="sm" color="gray.600">Limited Time</Text>
              </Flex>
              <Flex direction="column" align="center" p={4} bg="purple.50" borderRadius="lg">
                <Icon as={FaShippingFast} w={8} h={8} color="purple.500" mb={2} />
                <Text fontWeight="bold">Free Shipping</Text>
                <Text fontSize="sm" color="gray.600">On Orders Above Rs. 5000</Text>
              </Flex>
            </Grid>
          </Box>

          <Divider />

          {/* Sale Products Grid */}
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8}>
            {saleProducts.map((product) => (
              <MotionBox
                key={product.id}
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Box
                  as={RouterLink}
                  to={`/product/${product.id}`}
                  cursor="pointer"
                  _hover={{ textDecoration: "none" }}
                >
                  <Box
                    borderWidth="1px"
                    borderRadius="xl"
                    overflow="hidden"
                    bg={bgColor}
                    shadow="md"
                    transition="all 0.3s"
                    _hover={{
                      shadow: "xl",
                      transform: "translateY(-4px)",
                    }}
                  >
                    <Box position="relative">
                      <Image
                        src={product.image}
                        alt={product.name}
                        height="300px"
                        width="100%"
                        objectFit="contain"
                        p={4}
                        bg="white"
                      />
                      <Badge
                        position="absolute"
                        top={4}
                        right={4}
                        colorScheme="purple"
                        fontSize="md"
                        px={4}
                        py={2}
                        borderRadius="full"
                      >
                        {product.discount}% OFF
                      </Badge>
                    </Box>

                    <VStack p={6} align="start" spacing={3}>
                      <Heading size="md" color="purple.700">
                        {product.name}
                      </Heading>
                      <Text color={textColor} noOfLines={2}>
                        {product.description}
                      </Text>

                      {/* Fragrance Notes Preview */}
                      <Box w="100%" py={2}>
                        <Text fontSize="sm" fontWeight="bold" color="purple.500" mb={1}>
                          Fragrance Notes:
                        </Text>
                        <Text fontSize="sm" color="gray.600" noOfLines={1}>
                          {product.features[0].replace("Top notes: ", "")}
                        </Text>
                      </Box>

                      <HStack spacing={2}>
                        <Icon as={FiStar} color="yellow.400" />
                        <Text fontWeight="bold">{product.rating} / 5.0</Text>
                        <Text color="gray.500">({product.sold} sold)</Text>
                      </HStack>

                      <HStack justify="space-between" width="100%">
                        <VStack align="start" spacing={0}>
                          <Text
                            fontSize="xl"
                            fontWeight="bold"
                            color="purple.500"
                          >
                            Rs. {product.price.toLocaleString()}
                          </Text>
                          {product.originalPrice && (
                            <Text
                              fontSize="sm"
                              textDecoration="line-through"
                              color="purple.300"
                            >
                              Rs. {product.originalPrice.toLocaleString()}
                            </Text>
                          )}
                        </VStack>
                        <IconButton
                          aria-label="Add to wishlist"
                          icon={<FiHeart />}
                          variant="ghost"
                          color="purple.500"
                          size="lg"
                        />
                      </HStack>

                      <Button
                        leftIcon={<FiShoppingBag />}
                        colorScheme="purple"
                        width="full"
                        size="lg"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleAddToCart(product);
                        }}
                        _hover={{
                          transform: "translateY(-2px)",
                          shadow: "lg",
                          boxShadow: "0 4px 20px rgba(128, 90, 213, 0.6)",
                        }}
                        borderRadius="full"
                      >
                        Add to Cart
                      </Button>
                    </VStack>
                  </Box>
                </Box>
              </MotionBox>
            ))}
          </SimpleGrid>

          {/* Sale Features */}
          <Box mt={12} py={8} bg="purple.50" borderRadius="xl">
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8} px={8}>
              <Flex direction="column" align="center" textAlign="center">
                <Icon as={FiShield} w={8} h={8} color="purple.500" mb={4} />
                <Text fontWeight="bold" fontSize="lg" color="purple.700">
                  Secure Shopping
                </Text>
                <Text color="gray.600">
                  Safe and secure payment processing
                </Text>
              </Flex>
              <Flex direction="column" align="center" textAlign="center">
                <Icon as={FaCertificate} w={8} h={8} color="purple.500" mb={4} />
                <Text fontWeight="bold" fontSize="lg" color="purple.700">
                  Authentic Products
                </Text>
                <Text color="gray.600">
                  100% genuine luxury fragrances
                </Text>
              </Flex>
              <Flex direction="column" align="center" textAlign="center">
                <Icon as={FaShippingFast} w={8} h={8} color="purple.500" mb={4} />
                <Text fontWeight="bold" fontSize="lg" color="purple.700">
                  Fast Delivery
                </Text>
                <Text color="gray.600">
                  Quick and reliable shipping
                </Text>
              </Flex>
            </SimpleGrid>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
};

export default Sale;
